import { DateTime } from '@cheqroom/date-time';

import type { DynamicDateFilter, DynamicFilterValue, DynamicUserFilter } from './types';
import { FilterComparator } from './types';

export const parseDynamicFilters = (
	filters: Record<string, DynamicFilterValue | undefined>
): Record<string, unknown> => {
	let query: Record<string, unknown> = {};

	Object.entries(filters).forEach(([key, value]) => {
		const fieldName = key.replaceAll('.', '__');

		// A string choice (`'maintenance'`) is not a dynamic filter. `in` throws on primitives.
		if (!value || typeof value !== 'object') return;

		if (isUserFilter(value)) {
			query[`${fieldName}__in`] = normalizeSelected(value.selected);
			return;
		}

		if ([FilterComparator.EMPTY, FilterComparator.NOT_EMPTY].includes(value.comparator)) {
			query[`${fieldName}__exists`] = value.comparator === FilterComparator.NOT_EMPTY;
			return;
		}

		// Multiselect
		if ('selected' in value) {
			if (value.comparator === FilterComparator.EQ) {
				query[`${fieldName}__in`] = normalizeSelected(value.selected);
				return;
			}

			if (value.comparator === FilterComparator.NE) {
				query[`${fieldName}__nin`] = normalizeSelected(value.selected);
				return;
			}
		}

		if ('value' in value) {
			if (value.comparator === FilterComparator.STARTS_WITH) {
				query[`${fieldName}__istartswith`] = value.value;
				return;
			}

			if (value.comparator === FilterComparator.ENDS_WITH) {
				query[`${fieldName}__iendswith`] = value.value;
				return;
			}

			if (value.comparator === FilterComparator.CONTAINS) {
				query[`${fieldName}__icontains`] = value.value;
				return;
			}

			if (value.type === 'date') {
				query = { ...query, ...getDateQuery(fieldName, value) };
				return;
			}

			const queryKey = value.comparator === FilterComparator.EQ ? fieldName : `${fieldName}__${value.comparator}`;
			query[queryKey] = value.value;
		}
	});

	return query;
};

const isUserFilter = (value: DynamicFilterValue): value is DynamicUserFilter =>
	value.type === 'user' || value.type === 'customer';

/** `{ label, value }[]` from Role Select; `{ id, name }[]` from user pickers; `string[]` from ChoiceList. */
const normalizeSelected = (selected: unknown[]): string[] =>
	selected.map(toSelectedId).filter((entry): entry is string => typeof entry === 'string');

const toSelectedId = (entry: unknown): string | undefined => {
	if (typeof entry === 'string') return entry;
	if (!entry || typeof entry !== 'object') return undefined;
	if ('value' in entry && typeof entry.value === 'string') return entry.value;
	if ('id' in entry && typeof entry.id === 'string') return entry.id;
	return undefined;
};

export const getDateQuery = (key: string, dateFilter: DynamicDateFilter): Record<string, unknown> => {
	const getISODate = (boundary: 'start' | 'end'): string =>
		DateTime.fromISOString(dateFilter.value as string)
			[boundary === 'start' ? 'startOfDay' : 'endOfDay']()
			.toFloatingISOString();

	switch (dateFilter.comparator) {
		case FilterComparator.EMPTY:
			return {
				[`${key}__exists`]: false,
			};
		case FilterComparator.NOT_EMPTY:
			return {
				[`${key}__exists`]: true,
			};
		case FilterComparator.EQ:
			return {
				[`${key}__gte`]: getISODate('start'),
				[`${key}__lte`]: getISODate('end'),
			};
		case FilterComparator.GT:
			return {
				[`${key}__gte`]: getISODate('start'),
			};
		case FilterComparator.LT:
			return {
				[`${key}__lte`]: getISODate('end'),
			};
		default:
			return {};
	}
};

export default parseDynamicFilters;
